import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { deleteJSON, getJSON, patchJSON, postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { TemplatePreviewCard } from '@/pages/SystemEmailTemplatePreview'

export interface EmailTemplate {
  kind: string
  label: string
  subject: string
  greeting?: string
  intro?: string
  sign_off?: string
  footer_text?: string
  body?: string
  customized_fields: string[]
  revision: number
  latest_version_id: number | null
}

interface EmailTemplateVersion extends Fields {
  id: number
  number: number
  author_name: string
  created_at: string
}

interface TemplateListResponse {
  system_email_templates: EmailTemplate[]
}

interface TemplateResponse {
  system_email_template: EmailTemplate
}

export type Fields = Pick<
  EmailTemplate,
  'subject' | 'greeting' | 'intro' | 'sign_off' | 'footer_text' | 'body'
>

export function SystemEmailTemplatesPage() {
  const queryClient = useQueryClient()
  const [selectedKind, setSelectedKind] = React.useState('')
  const [fields, setFields] = React.useState<Fields | null>(null)
  const [notice, setNotice] = React.useState('')
  const templatesQuery = useQuery({
    queryKey: ['system-email-templates'],
    queryFn: () => getJSON<TemplateListResponse>('/api/v1/system_email_templates'),
    retry: false,
  })
  const templates = templatesQuery.data?.system_email_templates ?? []
  const selected = templates.find((template) => template.kind === selectedKind) ?? templates[0]

  React.useEffect(() => {
    if (!selected) return
    setSelectedKind(selected.kind)
    setFields(templateFields(selected))
  }, [selected])

  const save = useMutation({
    mutationFn: () =>
      patchJSON<TemplateResponse>(`/api/v1/system_email_templates/${selected.kind}`, {
        expected_revision: selected.revision,
        system_email_template: changedFields(fields!, selected),
      }),
    onSuccess: ({ system_email_template }) => {
      replaceTemplate(queryClient, system_email_template)
      void queryClient.invalidateQueries({
        queryKey: ['system-email-template-versions', system_email_template.kind],
      })
      setNotice('Email Template saved.')
    },
  })
  const revert = useMutation({
    mutationFn: () =>
      deleteJSON<TemplateResponse>(
        `/api/v1/system_email_templates/${selected.kind}?expected_revision=${selected.revision}`,
      ),
    onSuccess: ({ system_email_template }) => {
      replaceTemplate(queryClient, system_email_template)
      void queryClient.invalidateQueries({
        queryKey: ['system-email-template-versions', system_email_template.kind],
      })
      setNotice('Email Template reverted.')
    },
  })
  if (templatesQuery.isLoading) return <PageStatus>Loading Email Templates...</PageStatus>
  if (templatesQuery.isError || !selected || !fields)
    return <Alert>Email Templates unavailable</Alert>

  return (
    <div className="w-full max-w-[1200px] px-6 py-6">
      <header className="pb-4">
        <h1 className="text-heading-sm font-semibold text-foreground">Email Templates</h1>
      </header>
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card>
          <CardContent className="divide-y divide-border p-0" data-testid="email-template-sections">
            <TemplateEditorSection
              fields={fields}
              notice={notice}
              onFieldsChange={setFields}
              onRevert={() => revert.mutate()}
              onSave={() => {
                setNotice('')
                save.mutate()
              }}
              onSelect={setSelectedKind}
              revertPending={revert.isPending}
              saveError={save.error}
              savePending={save.isPending}
              selected={selected}
              templates={templates}
            />
            <TemplateHistorySection selected={selected} onRestore={setNotice} />
          </CardContent>
        </Card>
        <TemplatePreviewCard fields={fields} onNotice={setNotice} selected={selected} />
      </div>
    </div>
  )
}

function TemplateHistorySection({
  onRestore,
  selected,
}: {
  onRestore: (notice: string) => void
  selected: EmailTemplate
}) {
  const queryClient = useQueryClient()
  const history = useQuery({
    queryKey: ['system-email-template-versions', selected.kind],
    queryFn: () =>
      getJSON<{ system_email_template_versions: EmailTemplateVersion[] }>(
        `/api/v1/system_email_templates/${selected.kind}/versions`,
      ),
    retry: false,
  })
  const restore = useMutation({
    mutationFn: (versionId: number) =>
      postJSON<TemplateResponse>(
        `/api/v1/system_email_templates/${selected.kind}/versions/${versionId}/restore`,
        { expected_revision: selected.revision },
      ),
    onSuccess: ({ system_email_template }) => {
      replaceTemplate(queryClient, system_email_template)
      void queryClient.invalidateQueries({
        queryKey: ['system-email-template-versions', selected.kind],
      })
      onRestore('Email Template version restored.')
    },
    onError: (error) => onRestore(error.message),
  })
  const versions = history.data?.system_email_template_versions ?? []

  return (
    <section>
      <h2 className="px-4 pt-4 text-body font-semibold">Version history</h2>
      <div className="pt-3">
        {history.isLoading && <PageStatus>Loading version history...</PageStatus>}
        {history.isError && <Alert>Version history unavailable</Alert>}
        {!history.isLoading && !history.isError && versions.length === 0 && (
          <p className="p-4 text-body-sm text-muted-foreground">No published versions.</p>
        )}
        {[...versions].reverse().map((version) => (
          <div
            className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0"
            key={version.id}
          >
            <div className="min-w-0 flex-1">
              <p className="text-body-sm font-medium">Version {version.number}</p>
              <p className="text-caption text-muted-foreground">
                {version.author_name} · {new Date(version.created_at).toLocaleString()}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={restore.isPending || version.id === selected.latest_version_id}
              onClick={() => restore.mutate(version.id)}
            >
              Restore
            </Button>
          </div>
        ))}
      </div>
    </section>
  )
}

function TemplateEditorSection({
  fields,
  notice,
  onFieldsChange,
  onRevert,
  onSave,
  onSelect,
  revertPending,
  saveError,
  savePending,
  selected,
  templates,
}: {
  fields: Fields
  notice: string
  onFieldsChange: (fields: Fields) => void
  onRevert: () => void
  onSave: () => void
  onSelect: (kind: string) => void
  revertPending: boolean
  saveError: Error | null
  savePending: boolean
  selected: EmailTemplate
  templates: EmailTemplate[]
}) {
  return (
    <section className="p-4">
      <h2 className="pb-3 text-body font-semibold">Template fields</h2>
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault()
          onSave()
        }}
      >
        <FormField label="System Email" htmlFor="system-email-kind">
          <Select value={selected.kind} onValueChange={onSelect}>
            <SelectTrigger id="system-email-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {templates.map((template) => (
                <SelectItem key={template.kind} value={template.kind}>
                  {template.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <TemplateFields fields={fields} onChange={onFieldsChange} errors={saveError} />
        {notice && <p className="text-body-sm text-foreground">{notice}</p>}
        <div className="flex gap-2 pt-1">
          <Button type="submit" disabled={savePending}>
            Save Email Template
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={revertPending || selected.customized_fields.length === 0}
            onClick={onRevert}
          >
            Revert all fields
          </Button>
        </div>
      </form>
    </section>
  )
}

function TemplateFields({
  fields,
  onChange,
  errors,
}: {
  fields: Fields
  onChange: (fields: Fields) => void
  errors: Error | null
}) {
  const update =
    (field: keyof Fields) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange({ ...fields, [field]: event.target.value })
  return (
    <>
      <FormField
        label="Subject"
        htmlFor="template-subject"
        error={apiFieldMessages(errors, 'subject')[0]}
      >
        <Input id="template-subject" value={fields.subject} onChange={update('subject')} />
      </FormField>
      {fields.body !== undefined ? (
        <FormField label="Body" htmlFor="template-body" error={apiFieldMessages(errors, 'body')[0]}>
          <Textarea
            className="min-h-64"
            id="template-body"
            value={fields.body}
            onChange={update('body')}
          />
        </FormField>
      ) : (
        <SlotFields fields={fields} update={update} />
      )}
    </>
  )
}

function SlotFields({
  fields,
  update,
}: {
  fields: Fields
  update: (
    field: keyof Fields,
  ) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}) {
  return (
    <>
      <FormField label="Greeting" htmlFor="template-greeting">
        <Input id="template-greeting" value={fields.greeting ?? ''} onChange={update('greeting')} />
      </FormField>
      <FormField label="Intro" htmlFor="template-intro">
        <Textarea id="template-intro" value={fields.intro ?? ''} onChange={update('intro')} />
      </FormField>
      <FormField label="Sign-off" htmlFor="template-sign-off">
        <Input id="template-sign-off" value={fields.sign_off ?? ''} onChange={update('sign_off')} />
      </FormField>
      <FormField label="Footer text" htmlFor="template-footer-text">
        <Textarea
          id="template-footer-text"
          value={fields.footer_text ?? ''}
          onChange={update('footer_text')}
        />
      </FormField>
    </>
  )
}

function templateFields(template: EmailTemplate): Fields {
  if (template.body !== undefined) return { subject: template.subject, body: template.body }

  return {
    subject: template.subject,
    greeting: template.greeting,
    intro: template.intro,
    sign_off: template.sign_off,
    footer_text: template.footer_text,
  }
}

function changedFields(fields: Fields, original: EmailTemplate): Partial<Fields> {
  return Object.fromEntries(
    Object.entries(fields).filter(([field, value]) => value !== original[field as keyof Fields]),
  )
}

function replaceTemplate(
  queryClient: ReturnType<typeof useQueryClient>,
  replacement: EmailTemplate,
) {
  queryClient.setQueryData<TemplateListResponse>(['system-email-templates'], (current) => ({
    system_email_templates: (current?.system_email_templates ?? []).map((template) =>
      template.kind === replacement.kind ? replacement : template,
    ),
  }))
}

function PageStatus({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-6 py-6 text-body-sm text-muted-foreground" role="status">
      {children}
    </p>
  )
}
